import { configSingleStory, CustomMastheadProps } from "@pub/config";
import {
  ResponsiveIframe,
  ResponsiveImage,
  Typography,
  useCustomClassContext,
} from "@sphtech/dmg-design-system";
import cx from "classnames";
import { placeholderCrops } from "packages/design-system/src/components/Utility/placeholder";
import { useState } from "react";

import { moreIconPink200 } from "../icons/commonIcons";
import {
  copyIconTransparent,
  facebookIcon,
  mailIcon,
  shareIcon,
  whatsappIcon,
  xIcon,
} from "../icons/socialIcons";
import styles from "./mastheadnuyou.module.css";

const { Heading, Body, DateLine, DateTime } = Typography;

const sendSocialShareEvent = (socialnetwork: string, url: string) => {
  window.SPHMGTM.cmd.push(
    `send=${JSON.stringify({
      type: "event",
      category: "Share",
      action: socialnetwork,
      label: url,
      other: {
        data: {
          ux_element_name: "Headline",
          ux_element_type: "headline",
        },
      },
    })}`,
  );
};

const MastheadNUYOU = ({
  image,
  video,
  titleValue,
  articleHeading,
  excerpt,
  coverImageCaption,
  tags,
  authors,
  publishDate,
  placeholder = placeholderCrops.landscape,
}: CustomMastheadProps) => {
  const customClass = useCustomClassContext();
  const [isShare, setIsShare] = useState(false);
  const ARTICLE_TITLE = encodeURIComponent(titleValue || "");
  const ARTICLE_URL_RAW =
    typeof window !== "undefined" ? window.location.href : "";
  let ARTICLE_URL_CLEANED = ARTICLE_URL_RAW;
  if (ARTICLE_URL_RAW) {
    const url = new URL(ARTICLE_URL_RAW);
    if (url.searchParams.has("ref")) url.searchParams.delete("ref");
    ARTICLE_URL_CLEANED = url.toString();
  }
  const ARTICLE_URL = encodeURIComponent(ARTICLE_URL_CLEANED);

  return (
    <div className={styles.container}>
      <div className={styles.textContainer}>
        <div>
          {publishDate && (
            <DateLine>
              {configSingleStory.prefixForDateLine}
              <DateTime date={new Date(publishDate)} format="dd mname yyyy" />
            </DateLine>
          )}
          <Heading.Title>{articleHeading}</Heading.Title>
          <Body>{excerpt}</Body>
        </div>

        {authors && authors.length > 0 && (
          <div className={styles.authorsContainer}>
            {authors.map((author, index) => {
              const { text, href, title, image } = author;

              return (
                <div key={index} className={styles.authorBox}>
                  {image && (
                    <div className={styles.image}>
                      <ResponsiveImage
                        {...image}
                        srcWidth={64}
                        srcHeight={64}
                        fill="cover"
                      />
                    </div>
                  )}
                  <div>
                    {text && (
                      <a href={href} className={styles.name}>
                        <span>{text}</span>
                        {href && moreIconPink200}
                      </a>
                    )}
                    {title && <p className={styles.title}>{title}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tags && tags.length > 0 && (
          <div className={styles.tagsContainer}>
            {tags.map((tag, index) => (
              <a
                href={tag.path}
                key={index}
                className={cx(customClass?.tags?.secondary)}
              >
                {tag.name.replaceAll(" ", "")}
              </a>
            ))}
          </div>
        )}

        <div className={styles.shareContainer}>
          <button
            className={cx(styles.shareButton, isShare && styles.isShare)}
            onClick={() => {
              setIsShare(!isShare);
            }}
          >
            {shareIcon}
          </button>

          {isShare && (
            <div className={styles.shareBox}>
              <button
                className={styles.copy}
                onClick={() => {
                  setIsShare(false);
                  void navigator.clipboard.writeText(ARTICLE_URL);
                }}
              >
                <span>{copyIconTransparent}</span>
                <span>复制链接</span>
              </button>

              <hr />

              <p>分享文章:</p>
              <div className={styles.icons}>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${ARTICLE_URL}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    sendSocialShareEvent("facebook", ARTICLE_URL_CLEANED);
                  }}
                >
                  {facebookIcon}
                </a>
                <a
                  href={`https://api.whatsapp.com/send?text=${ARTICLE_URL}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    sendSocialShareEvent("whatsapp", ARTICLE_URL_CLEANED);
                  }}
                >
                  {whatsappIcon}
                </a>
                <a
                  href={`https://x.com/intent/tweet?url=${ARTICLE_URL}`}
                  target="_blank"
                  rel="noreferrer"
                  data-layout-position="socialshareicon"
                  onClick={() => {
                    sendSocialShareEvent("x", ARTICLE_URL_CLEANED);
                  }}
                >
                  {xIcon}
                </a>
                <a
                  href={`mailto:?subject=${ARTICLE_TITLE}&body=${ARTICLE_URL}`}
                  target="_blank"
                  rel="noreferrer"
                  data-layout-position="socialshareicon"
                  onClick={() => {
                    sendSocialShareEvent("email", ARTICLE_URL_CLEANED);
                  }}
                >
                  {mailIcon}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        {video?.url ? (
          <ResponsiveIframe src={video.url} loading="eager" />
        ) : (
          <ResponsiveImage {...(image || placeholder)} loading="eager" />
        )}

        {coverImageCaption && (
          <p className={styles.imageCaption}>{coverImageCaption}</p>
        )}
      </div>
    </div>
  );
};

export default MastheadNUYOU;
